import {
  PortaleDto,
  UserEsternoDto,
  getGetClientiQueryKey,
  getGetUsersQueryKey,
  useDeleteCliente,
  useGetAziende,
  useGetClienti,
  useGetPortali,
  useSignInAsUser,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import Typography from "@/elements/Typography";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import { usePosizioneFilterWithNavigation } from "@/hooks/usePosizioneFilterWithNavigation";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, TablePagination, TextField } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { keyBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ClienteDialog from "../ClienteDialog";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import PortaleChip from "../PortaleChip";

const columnHelper = createColumnHelper<UserEsternoDto>();

function ClientePage() {
  const { hasPermission, user } = useAuth();
  const { t } = useTranslation();

  const { isLoading, error, data } = useGetClienti();
  const { data: aziendeData } = useGetAziende();

  const {
    filters,
    changeFilter,
    filtersLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = usePosizioneFilterWithNavigation("/account_aziende", {
    enablePagination: true,
    enableTextFilter: true,
  });

  const aziendaById = useMemo(() => {
    if (!aziendeData?.data) {
      return {};
    }

    return keyBy(aziendeData.data, (x) => x.id);
  }, [aziendeData]);

  const [clientiDialogState, setClientiDialogState] = useState({
    open: false,
    cliente: null as UserEsternoDto | null,
  });

  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const { mutateAsync } = useDeleteCliente({
    mutation: {
      onSuccess: () => {
        toast.success(t("clientiPage.toast.eliminato"));
      },
      onError: () => {
        toast.error(t("errori.richiesta"));
      },
    },
  });

  const queryClient = useQueryClient();

  const { data: portaliData } = useGetPortali();

  const deleteCliente = useCallback(
    async (userId: number) => {
      try {
        await mutateAsync({ id: userId });
        queryClient.invalidateQueries({ queryKey: getGetUsersQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      } catch (err) {}
    },
    [mutateAsync, queryClient]
  );

  const { mutateAsync: impersonateAsync } = useSignInAsUser();
  const impersonaUtente = useCallback((id: number) => {
    impersonateAsync({ userIdToImpersonate: id }).then(() => {
      window.location.href = "/home";
      setTimeout(() => {
        window.location.reload();
      }, 10);
    });
  }, []);

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor("name", {
        header: t("clientiPage.colonne.nome"),
        size: undefined,
      }),
      columnHelper.accessor("lastName", {
        header: t("clientiPage.colonne.cognome"),
        size: undefined,
      }),
      columnHelper.accessor("email", {
        header: t("clientiPage.colonne.email"),
        size: undefined,
      }),
      columnHelper.accessor(
        (x) =>
          x.userRoles
            ?.map(
              (x) =>
                portaliData?.data?.find((p) => p.id === x.portaleId)?.nome ??
                null
            )
            .filter((x) => !!x)
            .join(", "),
        {
          header: t("clientiPage.colonne.portali"),
          size: undefined,
          cell: ({ row }) => {
            const portali = (row.original.userRoles
              ?.map((x) => portaliData?.data?.find((p) => p.id === x.portaleId))
              .filter((x) => !!x) ?? []) as PortaleDto[];

            return (
              <Box display="flex" gap={0.5}>
                {portali.map((p) => {
                  return <PortaleChip key={p.id} portale={p.id} />;
                })}
              </Box>
            );
          },
        }
      ),
      columnHelper.accessor(
        (x) =>
          x.userEsternoPermessi
            ?.map((x) => aziendaById[x.nodoId]?.nome)
            .filter((x) => !!x)
            .sort()
            .join(", "),
        {
          header: t("clientiPage.colonne.aziende"),
          size: undefined,
        }
      ),
      hasPermission("UsersEsterniChange") &&
        columnHelper.display({
          id: "buttons",
          header: t("clientiPage.colonne.azioni"),
          size: 250,
          maxSize: 250,
          cell: ({ row }) => {
            return (
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {user?.email === "admin@stark" && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={() => {
                      impersonaUtente(row.original.id!);
                    }}
                  >
                    {t("clientiPage.azioni.impersona")}
                  </Button>
                )}
                <IconButton
                  title={t("azioni.modifica")}
                  size="small"
                  color="primary"
                  onClick={() => {
                    setClientiDialogState({
                      open: true,
                      cliente: row.original,
                    });
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  title={t("azioni.elimina")}
                  size="small"
                  color="error"
                  onClick={() => {
                    askConfirmation({
                      title: t("clientiPage.confermaElimina.titolo"),
                      message: t("clientiPage.confermaElimina.messaggio", {
                        nome: row.original.name,
                        cognome: row.original.lastName,
                        email: row.original.email,
                      }),
                      okColor: "error",
                      onConfirm: () => {
                        return deleteCliente(row.original.id!);
                      },
                    });
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as unknown as Exclude<(typeof columns)[number], boolean>[];
  }, [
    setClientiDialogState,
    askConfirmation,
    deleteCliente,
    hasPermission,
    portaliData,
    aziendaById,
    user,
    t,
  ]);

  const allClienti = data?.data || [];

  const clientiFiltrati = useMemo(() => {
    if (!filters.textFilter) {
      return allClienti;
    }

    const filtro = filters.textFilter.toLowerCase().replace(/\./g, "");
    return allClienti.filter((cliente) => {
      if (
        cliente.name?.toLowerCase().includes(filtro) ||
        cliente.lastName?.toLowerCase().includes(filtro) ||
        cliente.email?.replace(/\./g, "")?.toLowerCase().includes(filtro)
      ) {
        return true;
      }

      const aziende = cliente.userEsternoPermessi
        ?.map((x) => aziendaById[x.nodoId]?.nome)
        ?.filter((x) => !!x)
        ?.join(" ")
        ?.toLowerCase()
        ?.replace(/\./g, "");

      return aziende?.includes(filtro) ?? false;
    });
  }, [allClienti, filters.textFilter, aziendaById]);

  const clientiPagedData = useMemo(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    return clientiFiltrati.slice(startIndex, endIndex);
  }, [clientiFiltrati, page, pageSize]);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <Box p={2}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" mr={2}>
          {t("clientiPage.titolo")}
        </Typography>
        <Box display="flex" gap={1} pt={1} flexWrap="wrap">
          {hasPermission("UsersEsterniChange") && (
            <Button
              size="small"
              color="light"
              onClick={() => {
                setClientiDialogState({
                  open: true,
                  cliente: null,
                });
              }}
            >
              {t("clientiPage.azioni.aggiungi")}
            </Button>
          )}
          <TextField
            placeholder={t("azioni.filtra")}
            value={filters.textFilter || ""}
            onChange={(e) => changeFilter("textFilter")(null, e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ ml: 1.5 }} /> }}
          />
        </Box>
      </Box>

      <Table
        columns={columns}
        data={clientiPagedData}
        sortable={true}
        paginated={false}
        initialSort={[{ id: "email", desc: false }]}
      />

      {clientiFiltrati.length > 0 && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          pt={2}
        >
          <TablePagination
            component="div"
            page={page}
            count={clientiFiltrati.length}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
            labelRowsPerPage={t("paginazione.righePerPagina")}
            labelDisplayedRows={({ from, to, count }) =>
              count === -1
                ? t("paginazione.righeSenzaTotale", { from, to })
                : t("paginazione.righe", { from, to, count })
            }
          />
        </Box>
      )}

      <ClienteDialog
        open={clientiDialogState.open}
        cliente={clientiDialogState.cliente}
        onClose={() => {
          setClientiDialogState((x) => ({
            ...x,
            open: false,
          }));
        }}
      />
      {confirmationDialog}
    </Box>
  );
}

export default ClientePage;
